<script>
  import { Notification, NOTIFICATION_STYLES, Loader, Success } from '@/shared/index'
  import notes from '@/components/report/notes'
  import dateMixin from '@/mixins/date'
  import { reportStatus } from '@/enums/reportStatus'
  import DropdownWithTags from '@/page/components/DropdownWithTags.vue'
  import SimplePagination from '@/components/elements/SimplePagination.vue'
  import PodiumMetaXploreReportService from '@/services/podiumMetaXploreReportService'
  import podiumReportService from '@/services/podiumReportService'
  import authService from '@/services/authService'
  import moment from 'moment'
  import ModalRecallReason from '@/components/models/ModalRecallReason.vue'
  import { productBrandMap, products } from '@/enums/productCodes'
  import ModalReprocess from '@/components/models/ModalReprocess.vue'
  import CopyUuid from '@/components/CopyUuid.vue'
  import { reportSnapshotMap } from '@modules/report-version-manager'

  const DEFAULT_STATUSES = ['Ready for Summary', 'Summary Submitted', 'Ready for Approval', 'Approved']

  const RESTRICTED_SAMPLE_ID_PREFIX = 'HCT'

  const DISPLAY_STATE_MAPPING = Object.freeze({
    GENERATED: 'Ready for Summary',
    EXPERT_SUMMARY_SUBMITTED: 'Summary Submitted',
    READY_FOR_APPROVAL: 'Ready for Approval',
    APPROVED: 'Approved',
    RELEASED: 'Released',
    RECALLED: 'Recalled'
  })

  export default {
    name: 'PodiumCobiomeResultRelease',
    components: { SimplePagination, notes, DropdownWithTags, ModalRecallReason, ModalReprocess, CopyUuid },
    mixins: [dateMixin],
    data() {
      return {
        reports: [],
        fields: [
          {
            key: 'reportId',
            sortable: true
          },
          {
            key: 'sampleId',
            label: 'Sample',
            sortable: true
          },
          {
            key: 'referralDate',
            label: 'Referred',
            sortable: true
          },
          {
            key: 'practitionerEmail',
            label: 'Practitioner Email',
            sortable: false
          },
          {
            key: 'currentResultState',
            label: 'Report Status',
            sortable: true
          },
          {
            key: 'releasedAt',
            label: 'Released',
            sortable: true
          },
          {
            key: 'approved'
          },
          {
            label: 'Product',
            key: 'productCode',
            sortable: true
          },
          {
            key: 'action'
          }
        ],
        visibleColumns: this.loadVisibleColumns([
          'reportId',
          'sampleId',
          'referralDate',
          'practitionerEmail',
          'currentResultState',
          'releasedAt',
          'approved',
          'productCode',
          'action'
        ]),
        selectedReports: [],
        selectedStatuses: this.loadSelectedFilterStatuses(DEFAULT_STATUSES),
        products,
        selectedProducts: [...products],
        query: '',
        isFiltered: false,
        displayNotes: false,
        activeReport: null,
        noteEditorVisible: false,
        page: 1,
        perPage: 50,
        sortBy: null,
        sortDesc: false,
        totalElements: 0,
        totalPages: 1,
        localSortingDisabled: true,
        isBusy: false,
        recallModalData: null,
        showRecallModal: false,
        selectedReportId: null,
        summary: null,
        summaryLoading: false,
        reprocessReport: null,
        showReprocessModal: false,
        partialResultFields: [
          { key: 'id', label: 'ID' },
          { key: 'sampleId', label: 'Sample' },
          { key: 'referralId', label: 'Referral' },
          { key: 'productId', label: 'Product' },
          { key: 'processingStatus', label: 'Status' },
          { key: 'processed', label: 'Processed' },
          { key: 'contentSnapshotId', label: 'Snapshot' },
          { key: 'cohortVersionName', label: 'Cohort' },
          { key: 'createdAt', label: 'Created' },
          { key: 'updatedAt', label: 'Updated' },
          { key: 'processingStartedAt', label: 'Started' },
          { key: 'processingCompletedAt', label: 'Completed' },
          { key: 'errorMessage', label: 'Error' }
        ],
        // If you think you should be here add your email in, assumed you know what
        // you are getting yourself into!
        allowedEmails: new Set([
          'anstin.babu@microba.com',
          'malcolm.sharman@microba.com',
          'nicola.angel@microba.com',
          'sam.brown@microba.com',
          'david.wood@microba.com',
          'zhenyao.luo@microba.com',
          'nadia.arias@microba.com',
          'adam.dilger@microba.com',
          'julian.anning@microba.com',
          'paul.berkman@microba.com',
          'lachlan.miller@microba.com',
          'staff@microba.com'
        ]),
        productBrandMap
      }
    },
    computed: {
      user() {
        return this.$store.state.GET_USER_RESPONSE
      },
      isAllowedToReprocess() {
        return this.allowedEmails.has(this.$store.state.GET_USER_RESPONSE?.email)
      },
      visibleReports() {
        if (this.isAllowedToReprocess) return this.reports
        return this.reports.filter((report) => !report.sampleId?.toUpperCase().startsWith(RESTRICTED_SAMPLE_ID_PREFIX))
      },
      visibleFields() {
        return this.fields.filter((f) => this.visibleColumns.includes(f.key))
      },
      columnOptions() {
        const seen = new Set()
        return this.fields.reduce((acc, f) => {
          if (!seen.has(f.key)) {
            seen.add(f.key)
            acc.push({ key: f.key, label: f.label || f.key })
          }
          return acc
        }, [])
      },
      allReportStatuses() {
        return reportStatus
      },
      approvedReports() {
        return this.visibleReports.filter((report) => report.reportStatus === this.allReportStatuses.APPROVED)
      },
      approvedReportsCount() {
        return this.approvedReports.length
      },
      areAllApprovedReportsSelected() {
        return this.selectedReports.length === this.approvedReports.length && this.approvedReports.length > 0
      },
      areSomeApprovedReportsSelected() {
        const approved = new Set(this.approvedReports)
        const selected = new Set(this.selectedReports)

        const intersection = approved.intersection(selected)

        return intersection.size > 0 && intersection.size < approved.size
      },
      releaseButtonClass() {
        return this.selectedReports.length ? 'release-button-selected' : 'release-button-disabled'
      },
      selectAllText() {
        return this.areSomeApprovedReportsSelected
          ? `${this.selectedReports.length} Selected`
          : `Select All Approved (${this.approvedReportsCount})`
      },
      paginationInfo() {
        const start = (this.page - 1) * this.perPage + 1
        const end = Math.min(this.page * this.perPage, this.totalElements)
        return `${start}-${end} of ${this.totalElements}`
      }
    },
    mounted() {
      this.getReports()
      this.$store.dispatch('getUser')
    },
    watch: {
      page: function () {
        this.getReports()
      },
      visibleColumns: function () {
        this.saveVisibleColumns()
      }
    },
    methods: {
      getProductReleaseVersion(row) {
        return reportSnapshotMap[row.item.contentSnapshotId]?.productVersion
      },
      formatDate(value, format = 'D MMM YYYY, h:mma') {
        if (!value) return '-'
        return moment.utc(value).add(moment().utcOffset(), 'm').format(format)
      },
      async getReports() {
        try {
          Loader.show()
          const pagedReports = await PodiumMetaXploreReportService.getReports(
            this.mapToReportStates(this.selectedStatuses),
            this.perPage,
            this.selectedProducts,
            this.page - 1,
            this.sortBy,
            this.sortDesc ? 'DESC' : 'ASC',
            this.query
          )

          this.isFiltered = this.query !== ''
          this.reports = pagedReports.content
          this.totalPages = pagedReports.totalPages
          this.totalElements = pagedReports.totalElements
          this.perPage = pagedReports.size
          this.page = pagedReports.number + 1
          this.isBusy = false

          this.updateStatusFilter()

          Loader.hide()
        } catch (error) {
          Notification({
            title: 'Failed',
            text: error.message,
            style: NOTIFICATION_STYLES.ERROR
          })
        }
      },
      mapToReportStates(selectedStates) {
        if (!selectedStates || selectedStates.length === 0) {
          return ['GENERATED']
        }

        const reverseMapping = Object.entries(DISPLAY_STATE_MAPPING).reduce((map, [key, value]) => {
          map[value] = key
          return map
        }, {})

        return selectedStates.map((state) => reverseMapping[state]).filter(Boolean)
      },

      getDisplayState(backendState) {
        return DISPLAY_STATE_MAPPING[backendState] || 'Error'
      },
      getReportStatusClass(status) {
        switch (status) {
          case reportStatus.GENERATED:
            return 'generated'
          case reportStatus.EXPERT_SUMMARY_SUBMITTED:
            return 'summary-submitted'
          case reportStatus.READY_FOR_APPROVAL:
            return 'ready-for-approval'
          case reportStatus.APPROVED:
            return 'approved'
          case reportStatus.RELEASED:
            return 'released'
          case reportStatus.RECALLED:
            return 'recalled'
          default:
            return 'invalid'
        }
      },
      getAllStatesDisplayValues() {
        const uniqueStatusValues = new Set(Object.values(DISPLAY_STATE_MAPPING))
        return Array.from(uniqueStatusValues)
      },
      selectAllRows() {
        this.$refs.selectableTable.selectAllRows()
      },
      clearSelected() {
        this.$refs.selectableTable.clearSelected()
      },
      onRowSelected(items) {
        this.selectedReports = items
      },
      isReportSelected(reportId) {
        return Boolean(this.selectedReports.find((report) => report.reportId === reportId))
      },
      toggleReportSelection(report, rowIndex) {
        const { reportId } = report
        if (this.isReportSelected(reportId)) {
          this.selectedReports = this.selectedReports.filter((report) => report.reportId !== reportId)
          this.$refs.selectableTable.unselectRow(rowIndex)
        } else {
          this.selectedReports.push(report)
          this.$refs.selectableTable.selectRow(rowIndex)
        }
      },
      async releaseSelectedReports() {
        try {
          if (!this.selectedReports.length) {
            return
          }

          const { isConfirmed: releaseSelectedReportResponse } = await Notification({
            title: 'Release the results for selected reports?',
            text: `Are you sure you want to release ${this.selectedReports.length} report(s)?`,
            style: NOTIFICATION_STYLES.QUESTION
          })

          if (!releaseSelectedReportResponse) {
            return
          }

          Loader.show()
          const errors = []

          for (const report of this.selectedReports) {
            try {
              const payload = {
                sampleId: report.sampleId,
                resultId: report.reportId,
                action: 'RELEASE',
                data: {
                  currentState: report.reportStatus,
                  transitionedBy: this.$store.getters.username || 'system',
                  transitionNotes: null
                }
              }

              const response = await podiumReportService.transitionReport(payload)

              if (!response.data.success) {
                errors.push(`Report ${report.reportId}: ${response.data.errorMessage || 'State transition failed'}`)
              }
            } catch (error) {
              errors.push(`Report ${report.reportId}: ${error.message}`)
            }
          }

          Loader.hide()

          if (errors.length) {
            await Notification({
              title: 'Partial Success',
              text: `Released ${this.selectedReports.length - errors.length} reports. Failed to release ${
                errors.length
              } reports.`,
              style: NOTIFICATION_STYLES.WARNING
            })
          } else {
            Success({
              text: `Successfully released ${this.selectedReports.length} reports.`,
              autoDismiss: true,
              timer: 1000
            })
          }

          await this.getReports()
        } catch (error) {
          Loader.hide()
          Notification({
            title: 'Failed',
            text: error.message,
            style: NOTIFICATION_STYLES.ERROR
          })
        }
      },
      async releaseReport(report) {
        try {
          const { reportId } = report
          const { isConfirmed: releaseSelectedReportResponse } = await Notification({
            title: `Release the results for Report ID ${reportId}?`,
            text: `Are you sure?`,
            style: NOTIFICATION_STYLES.QUESTION
          })
          if (releaseSelectedReportResponse) {
            Loader.show()

            const payload = {
              sampleId: report.sampleId,
              action: 'RELEASE',
              data: {
                currentState: report.reportStatus,
                transitionedBy: authService.getUsername(),
                transitionNotes: 'manually release report'
              }
            }

            const response = await podiumReportService.transitionReport(payload)

            if (!response || !response.data.success) {
              throw new Error(response.data.errorMessage || 'State transition failed')
            }

            Loader.hide()
            Success({ text: 'Report released.', autoDismiss: true, timer: 1000 })
            await this.getReports()
          }
        } catch (error) {
          Notification({
            title: 'Failed',
            text: error.message,
            style: NOTIFICATION_STYLES.ERROR
          })
        }
      },
      recallReport(report) {
        // Open the recall reason modal
        this.recallModalData = report
        this.showRecallModal = true
      },

      async handleRecallConfirm(payload) {
        try {
          const { reason } = payload
          const report = this.recallModalData
          const { reportId } = report

          // Show loader
          Loader.show()

          const apiPayload = {
            sampleId: report.sampleId,
            action: 'RECALL',
            data: {
              currentState: report.reportStatus,
              transitionedBy: authService.getUsername(),
              transitionNotes: reason // Use user-provided reason
            }
          }

          const trimmedReportId = reportId.replace(/-\d+$/, '')

          const response = await podiumReportService.transitionReport(apiPayload)

          if (!response || !response.data.success) {
            throw new Error(response.data.errorMessage || 'State transition failed')
          }

          Loader.hide()
          await Notification({
            title: 'Success',
            text: 'Report recalled.',
            style: NOTIFICATION_STYLES.SUCCESS
          })
          await this.getReports()
        } catch (error) {
          Loader.hide()
          Notification({
            title: 'Failed',
            text: error.message,
            style: NOTIFICATION_STYLES.ERROR
          })
        }
      },
      async toggleSummary(report) {
        const { reportId } = report

        if (this.selectedReportId === reportId) {
          this.selectedReportId = null
          this.summary = null
          return
        }

        this.selectedReportId = reportId
        this.summary = null
        this.summaryLoading = true

        try {
          this.summary = await PodiumMetaXploreReportService.getReportSummary(reportId)
        } catch (error) {
          this.selectedReportId = null
          Notification({
            title: 'Failed to load summary',
            text: error.message,
            style: NOTIFICATION_STYLES.ERROR
          })
        } finally {
          this.summaryLoading = false
        }
      },
      openReprocess(report) {
        this.reprocessReport = report
        this.showReprocessModal = true
      },
      onReprocessAccepted(accepted) {
        Notification({
          title: 'Reprocess queued',
          text: `Tracking ID ${accepted.trackingId} for sample ${accepted.resolvedSampleId}.`,
          style: NOTIFICATION_STYLES.SUCCESS
        })
      },
      latestState(version) {
        const transitions = version.stateTransitions || []
        return transitions.length ? transitions[transitions.length - 1].toState : '—'
      },
      sortingChanged({ sortBy, sortDesc }) {
        this.sortBy = sortBy
        this.sortDesc = sortDesc
        this.getReports()
      },
      updateStatusFilter() {
        this.saveSelectedFilterStatuses()
        const defaultStatusesStr = JSON.stringify([
          'Approved',
          'Ready for Approval',
          'Ready for Summary',
          'Summary Submitted'
        ])
        const selectedStr = JSON.stringify(this.selectedStatuses.sort())
        if (selectedStr !== defaultStatusesStr) this.isFiltered = true
      },
      resetQueryAndStatuses() {
        this.query = ''
        this.selectedStatuses = [...DEFAULT_STATUSES]
        this.getReports()
      },
      loadSelectedFilterStatuses(defaultStatuses) {
        const serializedStatuses = localStorage.getItem('resultRelease.filterStatuses')
        if (serializedStatuses) {
          return JSON.parse(serializedStatuses)
        }
        return [...defaultStatuses]
      },
      saveSelectedFilterStatuses() {
        localStorage.setItem('resultRelease.filterStatuses', JSON.stringify(this.selectedStatuses))
      },
      toggleSelectAllApprovedReports(checked) {
        this.selectedReports = checked ? [...this.approvedReports] : []
      },
      loadVisibleColumns(defaults) {
        const stored = localStorage.getItem('resultRelease.visibleColumns')
        if (stored) {
          return JSON.parse(stored)
        }
        return [...defaults]
      },
      saveVisibleColumns() {
        localStorage.setItem('resultRelease.visibleColumns', JSON.stringify(this.visibleColumns))
      }
    }
  }
</script>
<template>
  <div class="page container-fluid">
    <transition name="slide-fade">
      <div>
        <header class="inline-between inline-wrap">
          <h1 class="page-header">{{ $t('microbiomeExplorerResultRelease') }}</h1>
        </header>
        <transition name="slide-fade">
          <div class="box box-shadow stack">
            <div class="inline-zero form-inline">
              <b-form-input
                v-model="query"
                type="search"
                placeholder="Search Report ID, Sample ID, Practitioner Email"
                autofocus
                @keyup.enter="getReports"
              />
              <dropdown-with-tags
                class="col-md-2"
                v-model="selectedStatuses"
                :availableValues="getAllStatesDisplayValues()"
                button-text="Status"
                @value-changed="getReports"
              />
              <button class="btn btn-primary" @click="getReports">Go</button>
            </div>
            <div class="product-filter">
              <span class="product-filter-label">Products:</span>
              <b-form-checkbox
                v-for="product in products"
                :key="product"
                v-model="selectedProducts"
                :value="product"
                inline
              >
                {{ $t(product) }}
              </b-form-checkbox>
            </div>
            <div v-if="isFiltered">
              <button @click="resetQueryAndStatuses" class="btn btn-sm btn-link">Reset filter</button>
            </div>
            <div class="actions-container">
              <div class="bulk-actions">
                <div class="select-all">
                  <b-form-checkbox
                    :checked="areAllApprovedReportsSelected"
                    :indeterminate="areSomeApprovedReportsSelected"
                    class="report-release-checkbox"
                    @change="toggleSelectAllApprovedReports"
                  >
                    {{ selectAllText }}
                  </b-form-checkbox>
                </div>
                <b-button
                  :disabled="!selectedReports.length"
                  class="release-button"
                  :class="releaseButtonClass"
                  @click="releaseSelectedReports"
                >
                  Release Reports
                </b-button>
              </div>
              <div v-if="totalElements > 0" class="pagination-container">
                <span class="pagination-info">{{ paginationInfo }}</span>
                <SimplePagination
                  :total-items="totalElements"
                  :items-per-page="perPage"
                  :current-page.sync="page"
                  @update:currentPage="getReports"
                />
              </div>
            </div>
            <div class="column-toggle">
              <span class="column-toggle-label">Columns:</span>
              <b-form-checkbox
                v-for="col in columnOptions"
                :key="col.key"
                v-model="visibleColumns"
                :value="col.key"
                inline
              >
                {{ col.label }}
              </b-form-checkbox>
            </div>
            <b-table
              id="reports-table"
              ref="selectableTable"
              striped
              :items="visibleReports"
              :fields="visibleFields"
              show-empty
              :no-local-sorting="localSortingDisabled"
              :foot-clone="visibleReports.length > 20"
              @sort-changed="sortingChanged"
              @row-selected="onRowSelected"
              sticky-header="700px"
            >
              <template v-slot:cell(reportId)="row">
                <div class="inline-zero">
                  <b-form-checkbox
                    v-if="row.item.reportStatus === allReportStatuses.APPROVED"
                    class="report-checkbox"
                    :checked="isReportSelected(row.item.reportId)"
                    @change="toggleReportSelection(row.item, row.index)"
                  >
                  </b-form-checkbox>
                  <span v-else class="checkbox-placeholder"></span>

                  <router-link
                    :to="{
                      name: 'Podium Cobiome Report Preview',
                      params: {
                        sampleId: row.item.sampleId,
                        referralId: row.item.referralReference,
                        productCode: row.item.productCode,
                        reportId: row.item.reportId
                      }
                    }"
                    >{{ row.item.reportId }}
                  </router-link>
                </div>
              </template>
              <template v-slot:cell(approved)="row">
                <div v-if="row.item.reportStatus === allReportStatuses.APPROVED" class="inline-half">
                  <font-awesome-icon :icon="['fa', 'fa-check-circle']" />
                  {{ formatDate(row.item.approvedAt) }}
                </div>
              </template>
              <template v-slot:cell(currentResultState)="row">
                <p :class="['tag', getReportStatusClass(row.item.reportStatus)]">
                  {{ getDisplayState(row.item.reportStatus) }}
                </p>
              </template>

              <template v-slot:cell(referralDate)="row">
                {{ row.item.referralDate | formatDate('Do MMM YYYY') }}
              </template>

              <template v-slot:cell(releasedAt)="row">
                {{ row.item.releasedAt | formatDate('Do MMM YYYY') }}
              </template>

              <template v-slot:cell(productCode)="row">
                {{ productBrandMap[row.item.productCode] }}
                <template v-if="row.item.productCode === 'microbiome-explorer-gi-assessment'">
                  ({{ getProductReleaseVersion(row) }})
                </template>
              </template>
              <template v-slot:cell(action)="row">
                <div class="inline-half inline-wrap">
                  <router-link
                    class="btn btn-sm btn-outline-warning inline-half"
                    :to="{
                      name: 'Podium Cobiome Report Preview',
                      params: {
                        sampleId: row.item.sampleId,
                        referralId: row.item.referralReference,
                        productCode: row.item.productCode,
                        reportId: row.item.reportId
                      }
                    }"
                  >
                    View Report
                  </router-link>
                  <button
                    v-if="row.item.reportStatus === allReportStatuses.APPROVED"
                    class="font-weight-bold btn-sm btn btn-outline-danger inline-half"
                    @click="releaseReport(row.item)"
                  >
                    <font-awesome-icon :icon="['fa', 'fa-check']" />
                    Release Report
                  </button>
                  <button
                    v-if="[allReportStatuses.RELEASED, allReportStatuses.AMENDED].includes(row.item.reportStatus)"
                    class="font-weight-bold btn-sm btn btn-outline-danger inline-half"
                    @click="recallReport(row.item)"
                  >
                    <font-awesome-icon :icon="['fa', 'fa-check']" />
                    Recall Report
                  </button>
                  <button class="btn btn-sm btn-outline-primary inline-half" @click="toggleSummary(row.item)">
                    {{ selectedReportId === row.item.reportId ? 'Hide' : 'Summary' }}
                  </button>
                  <button
                    v-if="isAllowedToReprocess"
                    class="btn btn-sm btn-outline-warning inline-half"
                    @click="openReprocess(row.item)"
                  >
                    Reprocess
                  </button>
                </div>
              </template>
            </b-table>
          </div>
        </transition>
      </div>
    </transition>
    <b-sidebar
      id="report-notes"
      aria-controls="report-notes"
      :visible="displayNotes"
      bg-variant="white"
      width="400px"
      right
      shadow
      no-header
      @change="displayNotes = $event"
    >
      <template #default="{ hide }">
        <div v-if="activeReport" class="p-4">
          <h2 id="report-notes-no-header-title">
            Report ID {{ activeReport.reportId || '' }}
            <font-awesome-icon :icon="['fa', 'fa-times-circle']" class="float-right" @click="hide" />
          </h2>

          <p>
            Report was generated on the
            <span class="text-muted font-weight-bold">{{ formatDate(activeReport.generatedAt, 'Do MMM YYYY') }}.</span>
          </p>
          <notes
            :key="activeReport.reportId"
            :report-id="activeReport.reportId"
            :readonly="true"
            @reportDetailsChanged="getReports()"
          />
        </div>
      </template>
    </b-sidebar>

    <!-- Recall Reason Modal -->
    <ModalRecallReason :show.sync="showRecallModal" :report="recallModalData" @confirm="handleRecallConfirm" />

    <!-- Reprocess Modal -->
    <ModalReprocess :show.sync="showReprocessModal" :report="reprocessReport" @accepted="onReprocessAccepted" />

    <!-- Summary Modal -->
    <b-modal
      :visible="!!selectedReportId"
      :title="`Summary — ${selectedReportId}`"
      size="xl"
      scrollable
      hide-footer
      @hide="
        selectedReportId = null
        summary = null
      "
    >
      <div v-if="summaryLoading" class="py-4 text-center">Loading…</div>
      <div v-else-if="summary">
        <p class="text-muted">
          Sample {{ summary.sampleId }}
          <span v-if="summary.referralId"> · Referral {{ summary.referralId }}</span>
        </p>

        <h3 class="insight-heading">Questionnaire</h3>
        <p v-if="!summary.survey" class="text-muted">No questionnaire is associated with this referral.</p>
        <dl v-else class="survey-details">
          <dt>Survey</dt>
          <dd>{{ summary.survey.surveyName }} v{{ summary.survey.surveyVersion }} ({{ summary.survey.surveySlug }})</dd>
          <dt>Response UUID</dt>
          <dd>{{ summary.survey.responseUuid }}</dd>
          <dt>Submitted</dt>
          <dd>
            <span :class="['badge', summary.survey.submitted ? 'badge-success' : 'badge-warning']">
              {{ summary.survey.submitted ? 'Submitted' : 'In progress' }}
            </span>
            <span v-if="summary.survey.updatedAt" class="text-muted small ml-2">
              {{ formatDate(summary.survey.updatedAt) }}
            </span>
          </dd>
          <dt>Started</dt>
          <dd>{{ formatDate(summary.survey.createdAt) }}</dd>
          <dt>Last updated</dt>
          <dd>{{ formatDate(summary.survey.updatedAt) }}</dd>
          <dt>Locked</dt>
          <dd v-if="summary.survey.lockedAt">{{ formatDate(summary.survey.lockedAt) }}</dd>
          <dd v-else>—</dd>
          <dt>Content snapshot</dt>
          <dd class="text-monospace small">{{ summary.survey.contentSnapshotId }}</dd>
        </dl>

        <h3 class="insight-heading">Partial Results</h3>
        <b-table small striped :items="summary.partialResults" :fields="partialResultFields" show-empty responsive>
          <template v-slot:cell(id)="row">
            <CopyUuid :value="row.item.id" />
          </template>
          <template v-slot:cell(referralId)="row">
            <CopyUuid :value="row.item.referralId" />
          </template>
          <template v-slot:cell(createdAt)="row">{{ formatDate(row.item.createdAt) }}</template>
          <template v-slot:cell(updatedAt)="row">{{ formatDate(row.item.updatedAt) }}</template>
          <template v-slot:cell(contentSnapshotId)="row">
            <CopyUuid :value="row.item.contentSnapshotId" />
          </template>
          <template v-slot:cell(processingStartedAt)="row">{{ formatDate(row.item.processingStartedAt) }}</template>
          <template v-slot:cell(processingCompletedAt)="row">{{ formatDate(row.item.processingCompletedAt) }}</template>
        </b-table>

        <h3 class="insight-heading">Versions</h3>
        <div v-for="version in [...summary.versions].reverse()" :key="version.version" class="version-block">
          <div class="version-header">
            <strong>v{{ version.version }}</strong>
            <span class="text-muted">{{ formatDate(version.timestamp) }}</span>
            <span class="badge badge-secondary">{{ latestState(version) }}</span>
          </div>
          <div class="transitions">
            <div class="transition-row transition-header">
              <span>From</span>
              <span>To</span>
              <span>Time</span>
              <span>By</span>
              <span>Notes</span>
            </div>
            <div
              v-for="transition in [...version.stateTransitions].reverse()"
              :key="transition.id"
              class="transition-row"
            >
              <span class="transition-from">{{ transition.fromState }}</span>
              <span class="transition-to">→ {{ transition.toState }}</span>
              <span class="transition-time text-muted">{{ formatDate(transition.transitionTimestamp) }}</span>
              <span class="transition-by text-muted">{{ transition.transitionedBy || 'system' }}</span>
              <em v-if="transition.notes" class="transition-notes">{{ transition.notes }}</em>
              <span v-else />
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<style scoped lang="scss">
  @import '@/styles/reports/_metaxplore.scss';
</style>

<style scoped>
  .template-page main header {
    margin: 0;
    padding: 1.5rem 0;
  }

  header > *,
  .template-page main header .page-header {
    margin: 0 !important;
  }

  .box {
    overflow: auto;
  }

  td a {
    color: inherit;
  }

  td > .badge {
    font-size: 14px;
    width: fit-content;
  }

  .btn {
    white-space: nowrap;
  }

  .fa-check-circle {
    color: #5cd69f;
    font-size: 1.2rem;
  }

  .actions-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0;
  }

  .bulk-actions {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    background: white;
  }

  .pagination-container {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .select-all {
    display: flex;
    align-items: center;
    width: 180px;
  }

  .release-button {
    border: none;
    padding: 8px 16px;
    font-weight: 600;
    cursor: pointer;
  }

  .pagination-info {
    margin-right: 40px;
  }

  .release-button-disabled {
    background-color: #e7e7e7;
    color: black;
    cursor: not-allowed;
  }

  .release-button-selected {
    background-color: #e4e7f2;
    color: #4e5ca4;
    cursor: pointer;
  }

  /* The following styles are hacks to overwrite the custom-bootstrap theme colour. */
  .report-release-checkbox >>> .custom-control-label:before,
  .report-checkbox >>> .custom-control-label:before {
    background-color: white;
    border-radius: 3px;
  }

  .report-release-checkbox >>> .custom-control-input:checked + .custom-control-label:before,
  .report-checkbox >>> .custom-control-input:checked + .custom-control-label:before {
    background-color: #6e7bba;
    border-color: #6e7bba;
  }

  .report-release-checkbox >>> .custom-control-input:indeterminate + .custom-control-label:before,
  .report-checkbox >>> .custom-control-input:indeterminate + .custom-control-label:before {
    background-color: #6e7bba;
    border-color: #6e7bba;
  }

  .checkbox-placeholder {
    display: inline-block;
    width: 24px;
    height: 20px;
  }

  .product-filter {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 4px 0;
  }

  .product-filter-label {
    font-weight: 600;
    white-space: nowrap;
  }

  .insight-heading {
    font-size: 1rem;
    font-weight: 600;
    margin: 1rem 0 0.5rem;
  }

  .survey-details {
    display: grid;
    grid-template-columns: 160px 1fr;
    gap: 4px 12px;
    margin: 0;
  }

  .survey-details dt {
    font-weight: 600;
  }

  .survey-details dd {
    margin: 0;
  }

  .version-block {
    margin-bottom: 0.75rem;
  }

  .version-header > * {
    margin-right: 0.75rem;
  }

  .transitions {
    margin: 0.25rem 0;
  }

  .transition-header {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    color: #6c757d;
    border-bottom: 1px solid #dee2e6;
    padding-bottom: 4px;
    margin-bottom: 2px;
  }

  .transition-row {
    display: grid;
    grid-template-columns: minmax(180px, 2fr) minmax(180px, 2fr) minmax(160px, 2fr) minmax(0, 1fr) minmax(0, 2fr);
    gap: 0 1rem;
    align-items: baseline;
    padding: 2px 0;
    font-size: 0.875rem;
  }

  .transition-from,
  .transition-to {
    font-family: monospace;
    font-size: 0.8rem;
  }

  .transition-notes {
    color: #6c757d;
  }

  .column-toggle {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 4px 0;
    border-top: 1px solid #e7e7e7;
    margin-top: 4px;
  }

  .column-toggle-label {
    font-weight: 600;
    white-space: nowrap;
  }
</style>
